// StakeholderSelectionScreen.jsx - FIXED
import React, { useState, useEffect, useRef } from "react";
import { FaChevronCircleUp } from "react-icons/fa";
import { toast } from "react-hot-toast";

const StakeholderSelectionScreen = ({
  userId,
  subsectionId,
  onComplete,
  initialStakeholders,
  bossStakeholder,
  otherStakeholders,
  isLoading,
  onPreviousStep,
}) => {
  // ✅ Initialize with null, will be set in useEffect
  const [stakeholder1, setStakeholder1] = useState(null);
  const [stakeholder2, setStakeholder2] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  console.log("🎯 ========== StakeholderSelectionScreen Props ==========");
  console.log("bossStakeholder:", bossStakeholder);
  console.log("otherStakeholders:", otherStakeholders);
  console.log("otherStakeholders.length:", otherStakeholders?.length);
  console.log("initialStakeholders:", initialStakeholders);

  // ✅ Single effect to initialize both stakeholders
  useEffect(() => {
    console.log("🔄 Effect: Initializing stakeholders...");

      // ✅ Validate initialStakeholders
  const getValidInitialStakeholders = () => {
    if (!initialStakeholders || initialStakeholders.length !== 2) {
      return null;
    }

    const [s1, s2] = initialStakeholders;
    
    // Check if both stakeholders have SAME ID (invalid data!)
    if (s1 && s2 && s1.id === s2.id) {
      console.warn("⚠️ INVALID SAVED DATA: Both stakeholders have same ID!");
      console.warn("  Stakeholder 1:", s1.name, s1.id);
      console.warn("  Stakeholder 2:", s2.name, s2.id);
      console.warn("  → Ignoring saved data, will use fresh backend data");
      return null;
    }

    console.log("✅ Saved data is valid (different IDs)");
    return initialStakeholders;
  };
    
    const validInitialStakeholders = getValidInitialStakeholders();

    // Priority 1: Use valid saved selection if EXISTS AND HAS DATA
    if (validInitialStakeholders && validInitialStakeholders[0] !== null) {
      console.log("  → Using saved stakeholders (non-null)");
      setStakeholder1(validInitialStakeholders[0]);
      setStakeholder2(validInitialStakeholders[1] || null);
      setIsInitialized(true);
      return;
    }

    // Priority 2: Use fresh backend data (boss from backend)
    console.log("  → Using fresh backend data (boss from props)");
    setStakeholder1(bossStakeholder || null);
    setStakeholder2(null); // User will select this
    setIsInitialized(true);
    
  }, [bossStakeholder, initialStakeholders]);

  // ✅ Debug current state
  useEffect(() => {
    console.log("📊 Current State:");
    console.log("  ├─ Stakeholder 1:", stakeholder1?.name || "null", stakeholder1?.id || "");
    console.log("  └─ Stakeholder 2:", stakeholder2?.name || "null", stakeholder2?.id || "");
    
    // Warn if both have same ID
    if (stakeholder1 && stakeholder2 && stakeholder1.id === stakeholder2.id) {
      console.error("🚨 ERROR: Both stakeholders have SAME ID! This should not happen!");
    }
  }, [stakeholder1, stakeholder2]);

  const handleGetStarted = () => {
    console.log("🚀 Get Started clicked:");
    console.log("  ├─ Stakeholder 1 (Boss):", stakeholder1?.name, stakeholder1?.id);
    console.log("  ├─ Stakeholder 2 (Q1 Other):", stakeholder2?.name, stakeholder2?.id);
    console.log("  └─ Q1 Others available:", otherStakeholders?.length);

    // ✅ Check 1: Boss must be selected
    if (!stakeholder1) {
      toast.error("⚠️ Boss information is loading. Please wait...");
      return;
    }

    // ✅ Check 2: Both stakeholders cannot have SAME ID
    if (stakeholder1 && stakeholder2 && stakeholder1.id === stakeholder2.id) {
      console.error("🚨 ERROR: Both stakeholders have same ID!");
      toast.error("⚠️ Please select a different stakeholder for Stakeholder 2");
      return;
    }

    // ✅ Check 3: If Q1 stakeholders exist, one must be selected
    if (otherStakeholders && otherStakeholders.length > 0 && !stakeholder2) {
      toast.error("⚠️ Please select a second stakeholder from Q1 to continue");
      return;
    }

    // ✅ Check 4: If no Q1 stakeholders, allow proceeding with boss only
    if ((!otherStakeholders || otherStakeholders.length === 0) && !stakeholder2) {
      toast.success(
        "✅ No additional stakeholders found in Q1. Proceeding with your boss only.",
        { duration: 4000 }
      );
      console.log("✅ Proceeding with Boss only (no Q1 stakeholders available)");
      if (typeof onComplete === "function") {
        onComplete([stakeholder1, null]);
      }
      return;
    }

    // ✅ Check 5: Both stakeholders selected - normal flow
    if (stakeholder1 && stakeholder2) {
      console.log("✅ Proceeding with both stakeholders:");
      console.log("  ├─ Stakeholder 1:", stakeholder1.name, "(ID:", stakeholder1.id + ")");
      console.log("  └─ Stakeholder 2:", stakeholder2.name, "(ID:", stakeholder2.id + ")");
      
      if (typeof onComplete === "function") {
        onComplete([stakeholder1, stakeholder2]);
      }
    }
  };

  // Check if we can proceed
  const canProceed = 
    stakeholder1 && 
    (stakeholder2 || (otherStakeholders && otherStakeholders.length === 0));

  // Loading state
  if (isLoading || !isInitialized) {
    return (
      <div className="w-full max-w-4xl mx-auto px-4 py-4">
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#54B0AF] mx-auto mb-4"></div>
            <p className="text-gray-600 font-gotham">Loading stakeholders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-4">
      {/* Header */}
      <div className="mb-8">
        <div className="space-y-4 text-[#323232] font-gotham font-normal text-base md:text-[16px] leading-relaxed">
          <p>
            Let's explore what your boss truly values by identifying their
            interests, promotion KPIs, and core values. This will help you align
            better, build trust, and work more effectively with them. We'll also
            look at one other key stakeholder who is essential for your
            promotion.
          </p>

          <p>
            In this module, we'll use the "Stakeholder Value Map" to help you
            visualize your boss's priorities and how you can best support them.
            By understanding their motivations, you can tailor your approach and
            build a stronger working relationship.
          </p>
        </div>
      </div>

      {/* Stakeholder Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {/* Stakeholder 1 - BOSS (LOCKED) */}
        <StakeholderCard
          number={1}
          stakeholder={stakeholder1}
          color="purple"
          isLocked={true}
          title="YOUR DIRECT MANAGER"
        />

        {/* Stakeholder 2 - DROPDOWN (Q1 OTHERS) */}
        <StakeholderCard
          number={2}
          stakeholder={stakeholder2}
          color="orange"
          isLocked={false}
          availableStakeholders={otherStakeholders || []}
          onSelect={(selected) => {
            console.log("✅ User selected for Stakeholder 2:", selected.name);
            setStakeholder2(selected);
          }}
        />
      </div>

      {/* Warning Messages */}
      {!bossStakeholder && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 text-sm font-gotham">
            ⚠️ No boss stakeholder found. Please add a "Boss (Direct Manager)" in
            Interpersonal Map.
          </p>
        </div>
      )}

      {otherStakeholders && otherStakeholders.length === 0 && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-yellow-800 text-sm font-gotham">
            ⚠️ No other stakeholders available in Q1. You can proceed with just your boss, 
            or add more stakeholders to Q1 in the Interpersonal Map.
          </p>
        </div>
      )}

      {/* Get Started Button */}
      <div className="flex justify-start">
        <button
          onClick={handleGetStarted}
          disabled={!canProceed}
          className={`inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-medium text-lg md:text-xl font-gotham transition-all ${
            canProceed
              ? "bg-[#54B0AF] hover:bg-teal-600 shadow-lg hover:shadow-xl cursor-pointer"
              : "bg-gray-300 cursor-not-allowed"
          }`}
        >
          Get Started <FaChevronCircleUp className="rotate-90 ml-2" />
        </button>
      </div>
    </div>
  );
};

// 🎴 Stakeholder Card Component
const StakeholderCard = ({
  number,
  stakeholder,
  color,
  isLocked,
  availableStakeholders = [],
  onSelect,
  title,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isDropdownOpen]);

  const handleToggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLocked) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleSelectStakeholder = (option) => {
    if (!isLocked && onSelect) {
      console.log(`✅ Selected for Stakeholder ${number}:`, option.name, "(ID:", option.id + ")");
      onSelect(option);
      setIsDropdownOpen(false);
    }
  };

  const gradientClasses = {
    purple: "from-[#6A11CB] via-[#5B0FB3] to-[#4E04AE]",
    orange: "from-[#FF9A00] via-[#FF7A00] to-[#F27100]",
    blue: "from-blue-600 to-blue-800",
    green: "from-green-600 to-green-800",
    pink: "from-pink-600 to-pink-800",
    gray: "from-[#666666] to-[#666666]",
  };

  return (
    <div className="bg-white w-full max-w-[282px] mx-auto 2xl:min-h-[317px] rounded-[25px] shadow-md overflow-visible border border-[#D9D9D9] hover:shadow-lg transition-shadow relative">
      {/* Header */}
      <div
        className={`bg-gradient-to-r ${
          gradientClasses[color] || gradientClasses.gray
        } py-3 px-4 text-white text-center h-[120px] 2xl:h-[146px] relative rounded-t-[25px]`}
        // style={{ height: "146px" }}
      >
        <p className="text-sm md:text-[14px] font-medium font-gotham uppercase tracking-wide">
          STAKEHOLDER #{String(number).padStart(2, "0")}
        </p>

        {/* Avatar */}
        <div className="absolute left-1/2 transform -translate-x-1/2 top-12 2xl:top-12 w-[130px] h-[130px] 2xl:w-[163.977px] 2xl:h-[163.977px] border-1.5 2xl:border-[2.76px] border-[#E8E8E8] rounded-full bg-[#F1F1F1] shadow-xl flex items-center justify-center z-10">
          {stakeholder?.avatar ? (
            stakeholder.avatar.startsWith("http") ||
            stakeholder.avatar.startsWith("/static") ||
            stakeholder.avatar.startsWith("/media") ? (
              <img
                src={stakeholder.avatar}
                alt={stakeholder.name || "Stakeholder"}
                className="2xl:w-20 2xl:h-20 w-20 h-20 object-contain rounded-full"
              />
            ) : (
              <span className="text-6xl">{stakeholder.avatar}</span>
            )
          ) : (
            <span className="text-4xl text-gray-400">👤</span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="pt-[25%] 2xl:pt-20 px-6 pb-6 flex flex-col items-center">
        {/* ========== CARD TYPE 1: LOCKED (Boss) ========== */}
        {isLocked ? (
          stakeholder ? (
            <div className="text-center">
              <h3 className="text-xl md:text-[21px] 2xl:text-[23px] font-gotham font-medium text-[#1C1C1C] mb-1">
                {stakeholder.name}
              </h3>
              <p className="text-xs md:text-[14px] text-[#1C1C1C] font-gotham font-normal mt-1 uppercase tracking-wide">
                {stakeholder.role}
              </p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-sm text-red-500 font-medium">No Boss Found</p>
              <p className="text-xs text-gray-400 mt-2">
                Add boss in Interpersonal Map
              </p>
            </div>
          )
        ) : (
          /* ========== CARD TYPE 2: DROPDOWN (Q1 Others) ========== */
          <div className="relative w-full">
            <button
              ref={buttonRef}
              onClick={handleToggleDropdown}
              type="button"
              className="w-full px-4 py-3 border-2 border-[#54B0AF] rounded-lg text-gray-700 bg-white hover:bg-gray-50 flex items-center justify-between transition-colors"
            >
              <span className="text-sm font-medium font-gotham truncate">
                {stakeholder ? stakeholder.name : "Select Stakeholder"}
              </span>
              <svg
                className={`w-5 h-5 flex-shrink-0 transition-transform ${
                  isDropdownOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Show selected stakeholder's role */}
            {stakeholder && (
              <p className="text-xs md:text-[13px] text-[#1C1C1C] font-gotham font-normal mt-2 text-center uppercase tracking-wide">
                {stakeholder.role}
              </p>
            )}

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div
                ref={dropdownRef}
                className="absolute z-50 w-full bottom-full mb-2 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto"
              >
                {availableStakeholders && availableStakeholders.length > 0 ? (
                  availableStakeholders.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => handleSelectStakeholder(option)}
                      type="button"
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-3 ${
                        stakeholder?.id === option.id ? "bg-gray-100" : ""
                      }`}
                    >
                      <span className="text-2xl">
                        {option.avatar?.startsWith("http") ||
                        option.avatar?.startsWith("/static") ||
                        option.avatar?.startsWith("/media") ? (
                          <img
                            src={option.avatar}
                            alt={option.name}
                            className="w-8 h-8 object-contain rounded-full"
                          />
                        ) : (
                          <span className="text-2xl">{option.avatar}</span>
                        )}
                      </span>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 text-sm font-gotham">
                          {option.name}
                        </p>
                        <p className="text-xs text-gray-600 font-gotham">
                          {option.role}
                        </p>
                      </div>
                      {stakeholder?.id === option.id && (
                        <svg
                          className="w-5 h-5 text-[#54B0AF]"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-3 text-center text-gray-500 text-sm font-gotham">
                    No other stakeholders available in Q1
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StakeholderSelectionScreen;